import { useEffect, useState } from 'react';
import type { Location } from 'history';
import type { Logger } from '@archipro-website/logger';
import { Tracker } from '../../types';
import { TRACK_DOWNLOAD_ATTR, TRACK_REFERRAL_ATTR } from '../../constants';
import { useTrackDownloadClick } from './useTrackDownloadClick';
import { useTrackWebsiteReferralClick } from './useTrackWebsiteReferralClick';
import { TrackedUser } from '../../trackers/google-tag-manager/googleTagManager';

/**
 * Track external URL clicks (`Website`, `TargetedWebsite`) and downloads
 * (`DownloadsClick`) using the `data-track-referral` and `data-track-download`
 * attributes on anchor elements.
 * @param location The current location.
 * @param trackerInstance The tracker instance.
 * @param logger The logger instance.
 * @param user The logged-in or guest user.
 * @param onTrackDownload Callback when a download link is clicked.
 * @param onTrackWebsiteReferral Callback when an external URL link is clicked.
 */
export const useLogLinkClicks = (
    location: Location,
    trackerInstance: Tracker,
    logger: Logger,
    user?: TrackedUser,
    onTrackDownload?: Parameters<typeof useTrackDownloadClick>[3],
    onTrackWebsiteReferral?: Parameters<typeof useTrackWebsiteReferralClick>[2]
) => {
    const [trackedLinks, setTrackedLinks] = useState(new Set<string>());
    const trackDownload = useTrackDownloadClick(
        trackerInstance,
        logger,
        user,
        onTrackDownload
    );
    const trackWebsiteReferral = useTrackWebsiteReferralClick(
        trackerInstance,
        logger,
        onTrackWebsiteReferral
    );

    // We limit tracked links to one event per URL per page to prevent duplicate events.
    // Browsing between pages will reset this and allow the same event to be tracked again.
    useEffect(() => {
        setTrackedLinks((trackedLinks) => {
            // No need to update the state if the sets already empty. Avoid re-rendering.
            if (trackedLinks.size === 0) {
                return trackedLinks;
            }
            return new Set<string>();
        });
    }, [location]);

    useEffect(() => {
        const handleMouseDown = (event: MouseEvent) => {
            let target = event.target as HTMLElement;
            while (target && target !== document.body) {
                if (target.tagName === 'A') {
                    if (
                        event.button === 0 || // Left mouse button / mobile touch start
                        event.button === 1 || // Middle mouse button
                        event.button === 2 // Right mouse button
                    ) {
                        const url = new URL((target as HTMLAnchorElement).href);
                        url.searchParams.delete('_ap_pageid');
                        url.searchParams.delete('utm_source');
                        url.searchParams.delete('utm_medium');
                        url.searchParams.delete('utm_campaign');
                        url.searchParams.delete('__apid');
                        const href = url.toString();

                        if (trackedLinks.has(href)) {
                            // Don't track the same link twice in the same page.
                            return;
                        }

                        let eventTracked = false;

                        if (target.getAttribute(TRACK_DOWNLOAD_ATTR)) {
                            trackDownload(target, event.button);
                            eventTracked = true;
                        }

                        if (target.getAttribute(TRACK_REFERRAL_ATTR)) {
                            trackWebsiteReferral(target, event.button);
                            eventTracked = true;
                        }

                        if (eventTracked) {
                            setTrackedLinks((prev) => new Set(prev).add(href)); // Add to tracked links
                            return;
                        }
                    }
                }
                target = target.parentElement as HTMLElement;
            }
        };

        document.body.addEventListener('mousedown', handleMouseDown);

        return () => {
            document.body.removeEventListener('mousedown', handleMouseDown);
        };
    }, [trackedLinks, trackerInstance, trackDownload, trackWebsiteReferral]);
};
